import * as React from 'react'
import { useSelector } from 'react-redux'
import { Accordion, Typography as MIUTypography } from '@mui/material'
import { AccordionSummary, AccordionDetails } from '@mui/material'
import { Box, Stack, List } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RadioButtonCheckedTwoToneIcon from '@mui/icons-material/RadioButtonCheckedTwoTone'
import { ReactComponent as OrcidIcon } from '../../assets/icons/orcid.svg'
import { green, grey } from '@mui/material/colors'
import { styled } from '@mui/material/styles'
import { researchersNames } from '../../utils'
import { IconButton } from '@mui/material'
import ListItemRow from './ListItemRow'
import CustomizedBox from './CustomizedBox'
import _ from 'lodash'
import padrino from './arboles_apadrinandos.json'

const ONLYONE = 1

const Typography = styled(MIUTypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '0.8em',
  fontWeight: props.istitle ? 600 : 500,
  lineHeight: 1,
  color: props.islabel ? '#6A6A6A' : '#1B1C37',
  textAlign: props.islabel ? 'start' : 'center',
}))

const AccordionContent = ({ data }) => {
  const { geometry, properties } = data || {}
  const coordinates = Array.isArray(geometry?.coordinates) ? geometry.coordinates : []
  const hasCoordinates =
    Number.isFinite(Number(coordinates[0])) && Number.isFinite(Number(coordinates[1]))
  const {
    alive,
    code,
    commit,
    dateLog,
    recorded,
    treeVerified,
    inconveniences,
    site,
    specie,
    attributes,
    carbon,
  } = properties || {}
  const { name: siteName, parish, section, zoneType, siteType } = site || {}
  const { family, name: specieName, pseudonym, classifier } = specie || {}
  const { value: valueCarbon, unit: unitCarbon } = carbon || {}
  const dateFormated = new Date(dateLog)

  const authenticated = useSelector(state => state.login.authenticated)
  const userRol = authenticated?.user?.rol
  const canViewComment =
    userRol?.includes('investigador') || userRol?.includes('administrador')

  const uniqueIdRef = React.useRef(null)
  if (!uniqueIdRef.current) {
    uniqueIdRef.current = `accordion-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`
  }
  const finalUniqueId = uniqueIdRef.current

  let biomasa = parseFloat(valueCarbon || 0)
  let carbono = biomasa ? biomasa * 0.5 : 0
  let oxigeno = carbono ? (carbono * 32) / 12 : 0

  biomasa = biomasa.toFixed(2)
  carbono = carbono.toFixed(2)
  oxigeno = oxigeno.toFixed(2)

  let labelPseudonym = ''
  if (pseudonym) {
    if (pseudonym.length === ONLYONE) labelPseudonym = pseudonym?.[0]?.name
    else
      pseudonym.forEach((obj, i) => {
        if (i === 0) labelPseudonym = obj.name
        else labelPseudonym = `${labelPseudonym}, ${obj.name}`
      })
  }

  if (!Boolean(data) || data.length === 0) return null

  const padrinoInfo = padrino.find(item => item.codigo === code)

  return (
    <div style={{ paddingBottom: 20 }}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${finalUniqueId}-panel1`}
          id={`${finalUniqueId}-panel1`}>
          <Typography istitle={1}>UBICACIÓN DEL ÁRBOL</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List sx={{ py: 0 }}>
            <ListItemRow key="siteName" label="Sitio" value={siteName} width="25%" />

            {Boolean(parish?.id) ? (
              <ListItemRow
                key="parish"
                label="Parroquia"
                value={parish.name}
                width="25%"
              />
            ) : null}

            <ListItemRow key="section" label="Sección" value={section} width="25%" />

            {zoneType ? (
              <ListItemRow
                key="zoneType"
                label="Contexto vial"
                value={zoneType}
                width="25%"
              />
            ) : null}

            {siteType ? (
              <ListItemRow
                key="siteType"
                label="Emplazamiento"
                value={siteType}
                width="25%"
              />
            ) : null}

            {attributes &&
              attributes.map((obj, i) =>
                obj.metaFeature.key === 'altitud' ? (
                  <ListItemRow
                    key={`altitud-${i}}`}
                    label="Altitud"
                    value={`${obj.value} ${obj.metaFeature.u_medida}`}
                    width="25%"
                  />
                ) : null,
              )}
          </List>
          <Box spacing={1} sx={{ py: 1 }}>
            {hasCoordinates ? (
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
                <CustomizedBox label="Longitud:" />
                <CustomizedBox label="Latitud:" />
                <CustomizedBox valueL={_.floor(Number(coordinates[0]), 12)} bcolor={1} enableChip />
                <CustomizedBox valueL={_.floor(Number(coordinates[1]), 12)} bcolor={1} enableChip />
              </Box>
            ) : (
              <ListItemRow key="coordinates" label="Coordenadas" value="No disponible" width="35%" />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${finalUniqueId}-panel-apadrin`}
          id={`${finalUniqueId}-panel-apadrin`}>
          <Typography istitle={1}>SERVICIOS ECOSISTÉMICOS</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List sx={{ py: 0 }}>
            <ListItemRow
              key="biomasa"
              label="Biomasa"
              value={valueCarbon && biomasa ? `${biomasa} ${unitCarbon}` : 'Por estimar'}
              width="25%"
            />
            <ListItemRow
              key="carbono"
              label="Carbono"
              value={valueCarbon && carbono ? `${carbono} ${unitCarbon}` : 'Por estimar'}
              width="25%"
            />
            <ListItemRow
              key="oxigeno"
              label="Oxigeno"
              value={valueCarbon && oxigeno ? `${oxigeno} ${unitCarbon}` : 'Por estimar'}
              width="25%"
            />
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${finalUniqueId}-panel2`}
          id={`${finalUniqueId}-panel2`}>
          <Typography istitle={1}>DESCRIPCIÓN GENERAL</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List sx={{ py: 0 }}>
            {Boolean(pseudonym?.length) && (
              <ListItemRow
                key={`pseudonym`}
                label={pseudonym.length === 1 ? 'Nombre común' : 'Nombres comunes'}
                value={labelPseudonym}
              />
            )}
            <ListItemRow
              key="specieName"
              label="Nombre científico"
              value={[specieName, classifier]}
            />
            <ListItemRow key="family" label="Familia" value={family?.name} />
          </List>

          <Box spacing={1} sx={{ py: 1 }}>
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
              {attributes &&
                attributes.map((obj, i) => {
                  let valueFeature = parseFloat(obj.value).toFixed(2)
                  if (obj.metaFeature.u_medida)
                    valueFeature = `${valueFeature} ${obj.metaFeature.u_medida}`

                  const element =
                    obj.metaFeature.tipo_dato !== 'texto' &&
                    obj.metaFeature.key !== 'altitud' ? (
                      <React.Fragment key={`accordion-feature-${i}`}>
                        <CustomizedBox
                          label={`${obj.metaFeature.nombre}:`}
                          stylePropText={{ fontSize: 13 }}
                        />
                        <CustomizedBox
                          valueL={`${valueFeature}`}
                          enableChip
                          bcolor={2}
                          medium
                        />
                      </React.Fragment>
                    ) : null
                  return element
                })}
            </Box>
          </Box>

          <List sx={{ py: 0 }}>
            {attributes &&
              attributes.map((obj, i) => {
                const keyFeature = obj.metaFeature.key
                const key = `accordion-feature-text-${i}`
                const label = obj.metaFeature.nombre
                const value = obj.value
                if (keyFeature === 'longX' || keyFeature === 'latY') return null

                const element =
                  obj.metaFeature.tipo_dato === 'texto' ? (
                    <ListItemRow key={key} label={label} value={value} />
                  ) : null
                return element
              })}
            <ListItemRow
              key="alive"
              label="Estado actual"
              value={alive ? 'Vivo' : 'Muerto'}
              icon={
                <RadioButtonCheckedTwoToneIcon
                  sx={{
                    color: alive ? green[500] : grey[500],
                    fontSize: 14,
                    verticalAlign: 'top',
                  }}
                />
              }
            />
            <ListItemRow key="treeCode" label="Código" value={code} />
          </List>
        </AccordionDetails>
      </Accordion>

      {inconveniences &&
        inconveniences.map((dataObj, i) => {
          const { category, attributes: dataAttributes } = dataObj
          if (!Boolean(authenticated) && category === 'Acciones recomendadas') return null
          return (
            <Accordion key={`inconvenience-${i}`}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${finalUniqueId}-panel-inconvenience${i}`}
                id={`${finalUniqueId}-panel-inconvenience${i}`}>
                <Typography istitle={1}>{_.toUpper(category)}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List sx={{ py: 0 }}>
                  {dataAttributes.map(obj => {
                    const keyElement = Object.keys(obj)[0]
                    const value = obj[keyElement].value
                    const label = obj[keyElement].label
                    const categoryKey = ['Estado fitosanitario', 'Riesgos potenciales']
                    const belongsToCategory = _.filter(
                      categoryKey,
                      k => category === k,
                    ).length
                    const element = belongsToCategory ? (
                      <ListItemRow
                        key={keyElement}
                        label={label}
                        value={value}
                        width={label.length < 22 ? '25%' : '60%'}
                      />
                    ) : (
                      <Stack key={keyElement} direction="row" sx={{ pb: 0.7 }}>
                        <CustomizedBox
                          valueL={value ? value : label}
                          enableChip
                          bcolor="#EDEDED"
                        />
                      </Stack>
                    )
                    return element
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          )
        })}
      {/* Nuevo acordeón para apadrinamiento */}
      {padrinoInfo && (
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${finalUniqueId}-panel-apadrinado`}
            id={`${finalUniqueId}-panel-apadrinado`}>
            <Typography istitle={1}>APADRINAMIENTO</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List sx={{ py: 0 }}>
              <ListItemRow
                key="nombre-apadrinado"
                label="Apadrinado por"
                value={padrinoInfo.nombre}
              />
            </List>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${finalUniqueId}-panel3-info`}
          id={`${finalUniqueId}-panel3-info`}>
          <Typography istitle={1}>INFORMACIÓN DEL REGISTRO</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List sx={{ py: 0 }}>
            {Boolean(authenticated) && (
              <ListItemRow
                key="verivicatedData"
                label="Verificación de datos"
                value={treeVerified ? 'Verificada' : 'No verificada'}
                styleTextValue={{
                  textAlign: 'end',
                  color: treeVerified ? '#14aa6a' : '#aa1414',
                  fontWeight: 550,
                }}
              />
            )}
            <ListItemRow
              key="recorded"
              label="Nombre del colector"
              value={recorded}
              styleTextValue={{ textAlign: 'end' }}
            />

            <ListItemRow
              key="dateLog"
              label="Fecha de registro"
              value={dateFormated}
              styleTextValue={{ textAlign: 'end' }}
            />
          </List>
        </AccordionDetails>
      </Accordion>

      {canViewComment && (
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${finalUniqueId}-panel-comentario`}
            id={`${finalUniqueId}-panel-comentario`}>
            <Typography istitle={1}>COMENTARIO</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MIUTypography
              variant="body2"
              sx={{ fontSize: '0.8em', color: commit ? '#1B1C37' : '#6A6A6A', px: 0.5 }}>
              {commit || 'Sin comentarios'}
            </MIUTypography>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${finalUniqueId}-panel3-researchers`}
          id={`${finalUniqueId}-panel3-researchers`}>
          <Typography istitle={1}>INVESTIGADORES</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List sx={{ py: 0 }}>
            {_.map(researchersNames, obj => (
              <ListItemRow
                key={`researcher-name-${obj.name}`}
                value={obj.name}
                styleTextValue={{
                  textAlign: 'start',
                  display: 'flex',
                  justifyContent: 'space-between',
                  lineHeight: 2,
                  marginBottom: '-10px',
                }}
                icon={
                  obj.orcid && (
                    <Box>
                      <IconButton
                        target="_blank"
                        href={obj.orcid}
                        aria-label={obj.name}
                        size="small"
                        sx={{ verticalAlign: 'baseline', p: '4px' }}>
                        <OrcidIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  )
                }
              />
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

export default AccordionContent
